'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import type { PayPalCheckout } from 'braintree-web';

// =============================================================================
// Types
// =============================================================================

export type PayPalStatus = 'idle' | 'initializing' | 'ready' | 'tokenizing' | 'error';

export interface PayPalHookError {
  code: string;
  message: string;
  userMessage: string;
  recoverable: boolean;
}

export interface UsePayPalCheckoutOptions {
  containerId: string;
  onReady?: () => void;
  onApprove?: (nonce: string) => void;
  onCancel?: () => void;
  onError?: (error: PayPalHookError) => void;
}

export interface UsePayPalCheckoutReturn {
  status: PayPalStatus;
  isReady: boolean;
  isLoading: boolean;
  error: PayPalHookError | null;
  retry: () => void;
}

// =============================================================================
// Constants
// =============================================================================

const INIT_TIMEOUT = 15000;
const MAX_RETRIES = 2;

// =============================================================================
// Error Mapping
// =============================================================================

const userMessages: Record<string, string> = {
  PAYPAL_POPUP_CLOSED: 'PayPal window was closed. Please try again.',
  PAYPAL_ACCOUNT_TOKENIZATION_FAILED: 'Unable to connect to PayPal. Please try again.',
  PAYPAL_FLOW_FAILED: 'PayPal authorization failed. Please try again.',
  PAYPAL_INVALID_RESOURCE: 'PayPal configuration error. Please try again later.',
  CLIENT_AUTHORIZATION_INVALID: 'Payment system configuration error. Please try again later.',
  CLIENT_GATEWAY_NETWORK: 'Unable to connect to payment system. Please try again.',
  CLIENT_REQUEST_TIMEOUT: 'Request timed out. Please try again.',
  PAYPAL_CANCELED: 'PayPal payment was cancelled.',
};

const recoverableCodes = new Set([
  'PAYPAL_POPUP_CLOSED',
  'PAYPAL_ACCOUNT_TOKENIZATION_FAILED',
  'PAYPAL_FLOW_FAILED',
  'CLIENT_GATEWAY_NETWORK',
  'CLIENT_REQUEST_TIMEOUT',
  'PAYPAL_CANCELED',
]);

function mapPayPalError(err: unknown): PayPalHookError {
  // Handle timeout/abort errors
  if (err instanceof Error) {
    if (err.message.includes('timeout') || err.name === 'AbortError') {
      return {
        code: 'CLIENT_REQUEST_TIMEOUT',
        message: err.message,
        userMessage: userMessages['CLIENT_REQUEST_TIMEOUT'],
        recoverable: true,
      };
    }
    if (err.message.includes('network') || err.message.includes('fetch')) {
      return {
        code: 'CLIENT_GATEWAY_NETWORK',
        message: err.message,
        userMessage: userMessages['CLIENT_GATEWAY_NETWORK'],
        recoverable: true,
      };
    }
    if (err.message.includes('closed') || err.message.includes('cancel')) {
      return {
        code: 'PAYPAL_POPUP_CLOSED',
        message: err.message,
        userMessage: userMessages['PAYPAL_POPUP_CLOSED'],
        recoverable: true,
      };
    }
  }

  // Handle Braintree SDK errors
  const btErr = err as { code?: string; message?: string };
  if (btErr.code) {
    return {
      code: btErr.code,
      message: btErr.message || btErr.code,
      userMessage: userMessages[btErr.code] || 'An error occurred. Please try again.',
      recoverable: recoverableCodes.has(btErr.code),
    };
  }

  // Fallback for unknown errors
  return {
    code: 'UNKNOWN_ERROR',
    message: err instanceof Error ? err.message : 'Unknown error',
    userMessage: 'An unexpected error occurred. Please try again.',
    recoverable: true,
  };
}

// =============================================================================
// Hook
// =============================================================================

export function usePayPalCheckout(options: UsePayPalCheckoutOptions): UsePayPalCheckoutReturn {
  const [status, setStatus] = useState<PayPalStatus>('idle');
  const [error, setError] = useState<PayPalHookError | null>(null);

  const paypalCheckoutRef = useRef<PayPalCheckout | null>(null);
  const mountedRef = useRef(true);
  const initAttemptRef = useRef(0);
  const initializingRef = useRef(false);
  const buttonsRenderedRef = useRef(false);
  const optionsRef = useRef(options);

  // Keep options ref updated
  optionsRef.current = options;

  // Computed values
  const isReady = status === 'ready';
  const isLoading = status === 'initializing' || status === 'tokenizing';

  // Initialize PayPal Checkout
  const initialize = useCallback(async () => {
    if (initializingRef.current) return;

    const tokenizationKey = process.env.NEXT_PUBLIC_BRAINTREE_TOKENIZATION_KEY;
    if (!tokenizationKey) {
      const configError: PayPalHookError = {
        code: 'CLIENT_AUTHORIZATION_INVALID',
        message: 'Braintree tokenization key not configured',
        userMessage: userMessages['CLIENT_AUTHORIZATION_INVALID'],
        recoverable: false,
      };
      setError(configError);
      setStatus('error');
      optionsRef.current.onError?.(configError);
      return;
    }

    // Validate DOM container exists
    const container = document.getElementById(optionsRef.current.containerId);
    if (!container) {
      const domError: PayPalHookError = {
        code: 'PAYPAL_INVALID_RESOURCE',
        message: `Missing DOM container: #${optionsRef.current.containerId}`,
        userMessage: 'Payment form could not be loaded. Please refresh the page.',
        recoverable: true,
      };
      setError(domError);
      setStatus('error');
      optionsRef.current.onError?.(domError);
      return;
    }

    setStatus('initializing');
    setError(null);
    initializingRef.current = true;

    // Create timeout
    const timeoutId = setTimeout(() => {
      if (mountedRef.current && initializingRef.current) {
        initializingRef.current = false;
        const timeoutError = mapPayPalError(new Error('Initialization timed out'));
        setError(timeoutError);
        setStatus('error');
        optionsRef.current.onError?.(timeoutError);
      }
    }, INIT_TIMEOUT);

    try {
      // Dynamic import to avoid SSR issues
      const braintree = await import('braintree-web');

      if (!mountedRef.current) {
        clearTimeout(timeoutId);
        return;
      }

      // Create Braintree client
      const clientInstance = await braintree.client.create({
        authorization: tokenizationKey,
      });

      if (!mountedRef.current) {
        clearTimeout(timeoutId);
        return;
      }

      // Create PayPal Checkout instance
      const paypalCheckoutInstance = await braintree.paypalCheckout.create({
        client: clientInstance,
      });

      if (!mountedRef.current) {
        clearTimeout(timeoutId);
        await paypalCheckoutInstance.teardown();
        return;
      }

      paypalCheckoutRef.current = paypalCheckoutInstance;

      // Load PayPal SDK with vault configuration
      await paypalCheckoutInstance.loadPayPalSDK({
        vault: true,
      });

      if (!mountedRef.current) {
        clearTimeout(timeoutId);
        await paypalCheckoutInstance.teardown();
        return;
      }

      // Access the global paypal object loaded by the SDK
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const paypal = (window as any).paypal as {
        Buttons: (config: {
          fundingSource: string;
          createBillingAgreement: () => Promise<string>;
          onApprove: (data: { billingToken?: string; payerID?: string }) => Promise<void>;
          onCancel: () => void;
          onError: (err: Error) => void;
        }) => { render: (selector: string) => Promise<void> };
        FUNDING: { PAYPAL: string };
      };

      if (!paypal || !paypal.Buttons) {
        throw new Error('PayPal SDK failed to load');
      }

      // Render PayPal buttons
      await paypal
        .Buttons({
          fundingSource: paypal.FUNDING.PAYPAL,

          createBillingAgreement: () => {
            // TypeScript types expect an enum but Braintree SDK accepts string at runtime
            return paypalCheckoutInstance.createPayment({
              flow: 'vault',
            } as Parameters<typeof paypalCheckoutInstance.createPayment>[0]);
          },

          onApprove: async (data) => {
            if (!mountedRef.current) return;

            setStatus('tokenizing');

            try {
              // PayPal SDK passes payerID but Braintree types expect payerId
              const tokenizeData = {
                payerId: data.payerID || '',
                billingToken: data.billingToken,
              };
              const payload = await paypalCheckoutInstance.tokenizePayment(tokenizeData);

              if (!mountedRef.current) return;

              setStatus('ready');
              optionsRef.current.onApprove?.(payload.nonce);
            } catch (tokenizeErr) {
              if (!mountedRef.current) return;

              const hookError = mapPayPalError(tokenizeErr);
              setError(hookError);
              setStatus('ready'); // Stay ready so user can retry
              optionsRef.current.onError?.(hookError);
            }
          },

          onCancel: () => {
            console.debug('Paypal payment cancelled by user');
            if (!mountedRef.current) return;
            // Don't set error state for cancellation - just notify via callback
            optionsRef.current.onCancel?.();
          },

          onError: (err) => {
            console.error('Paypal payment error', err);
            if (!mountedRef.current) return;

            const hookError = mapPayPalError(err);
            setError(hookError);
            setStatus('ready'); // Stay ready so user can retry
            optionsRef.current.onError?.(hookError);
          },
        })
        .render(`#${optionsRef.current.containerId}`);

      clearTimeout(timeoutId);

      if (!mountedRef.current) {
        await paypalCheckoutInstance.teardown();
        return;
      }

      buttonsRenderedRef.current = true;
      initializingRef.current = false;
      setStatus('ready');
      initAttemptRef.current = 0;
      optionsRef.current.onReady?.();
    } catch (err) {
      clearTimeout(timeoutId);
      initializingRef.current = false;

      if (!mountedRef.current) return;

      const shouldRetry = initAttemptRef.current < MAX_RETRIES;

      if (shouldRetry) {
        initAttemptRef.current++;
        // Exponential backoff: 500ms, 1000ms, 2000ms
        const delay = Math.pow(2, initAttemptRef.current) * 250;
        setTimeout(() => {
          if (mountedRef.current) {
            setStatus('idle'); // Reset to trigger re-init
          }
        }, delay);
      } else {
        const hookError = mapPayPalError(err);
        setError(hookError);
        setStatus('error');
        optionsRef.current.onError?.(hookError);
      }
    }
  }, []);

  // Retry initialization
  const retry = useCallback(() => {
    if (status === 'error') {
      initAttemptRef.current = 0;
      buttonsRenderedRef.current = false;
      setError(null);
      setStatus('idle');
    }
  }, [status]);

  // Initialize on mount (with small delay for DOM readiness)
  useEffect(() => {
    // Don't re-initialize if buttons are already rendered
    if (buttonsRenderedRef.current) return;

    const initTimer = setTimeout(() => {
      if (mountedRef.current && status === 'idle') {
        initialize();
      }
    }, 50);

    return () => {
      clearTimeout(initTimer);
    };
  }, [initialize, status]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      const instance = paypalCheckoutRef.current;
      if (instance) {
        instance.teardown().catch((err) => {
          console.warn('PayPal checkout teardown error:', err);
        });
        paypalCheckoutRef.current = null;
      }
    };
  }, []);

  return {
    status,
    isReady,
    isLoading,
    error,
    retry,
  };
}
